import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../../components/Layout';
import { useAuth } from '../../context/AuthContext';
import { stockTransferService } from '../../services/stockTransferService';
import warehouseService from '../../services/warehouseService';
import { apiService } from '../../services/api';
import { CreateStockTransferRequest } from '../../types';
import './StockTransferPage.css';
import { parseApiError } from '../../utils/errorUtils';
import { ArrowLeft, Save, AlertCircle, Plus, X } from 'lucide-react';

// ─── Local types ──────────────────────────────────────────────────────────────

interface WarehouseOption { id: number; name: string; }
interface ProductOption   { id: number; name: string; sku: string; unitOfMeasure?: string; }
interface UnitOption      { id: number; unitName: string; }

interface LineItem {
  productId:         number;
  unitId:            number;
  quantityRequested: number;
  remarks:           string;
  stockTransferItemId?: number;
}

interface FieldErrors {
  sourceWarehouse?:      string;
  destinationWarehouse?: string;
  transferDate?:         string;
  expectedArrivalDate?:  string;
  items?:                string;
}

const emptyLine = (): LineItem => ({
  productId:         0,
  unitId:            0,
  quantityRequested: 0,
  remarks:           '',
});

// ─── Component ────────────────────────────────────────────────────────────────

const StockTransferCreatePage: React.FC = () => {
  const navigate      = useNavigate();
  const { id }        = useParams<{ id?: string }>();
  const { t }         = useTranslation();
  const { user }      = useAuth();
  const isEdit        = !!id;

  // ── Header fields ──────────────────────────────────────────────────────────

  const [sourceWarehouseId,      setSourceWarehouseId]      = useState<number>(0);
  const [destinationWarehouseId, setDestinationWarehouseId] = useState<number>(0);
  const [transferDate,           setTransferDate]           = useState(new Date().toISOString().split('T')[0]);
  const [expectedArrivalDate,    setExpectedArrivalDate]    = useState('');
  const [notes,                  setNotes]                  = useState('');

  // ── Line items ─────────────────────────────────────────────────────────────

  const [items, setItems] = useState<LineItem[]>([emptyLine()]);

  // ── Dropdown data ──────────────────────────────────────────────────────────

  const [warehouses, setWarehouses] = useState<WarehouseOption[]>([]);
  const [products,   setProducts]   = useState<ProductOption[]>([]);
  const [units,      setUnits]      = useState<UnitOption[]>([]);

  // ── UI state ───────────────────────────────────────────────────────────────

  const [loading,     setLoading]     = useState(false);
  const [initialLoad, setInitialLoad] = useState(isEdit);
  const [error,       setError]       = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});

  // ── Load dropdowns (+ edit data) ──────────────────────────────────────────

  useEffect(() => {
    const load = async () => {
      try {
        // warehouseService uses the correct /organization/warehouses route.
        // Products and units use api/v1/[controller] so their short paths work.
        const [whRes, prodRes, unitRes] = await Promise.all([
          warehouseService.getWarehouses(1, 200),
          apiService.get<any>('/products',   { pageSize: 1000, isActive: true }),
          apiService.get<any>('/units',      { pageSize: 200 }),
        ]);

        setWarehouses(whRes.data?.data ?? []);

        const mapProd = (d: any): ProductOption[] =>
          (d?.data?.data ?? d?.data ?? []).map((p: any) => ({
            id: p.id ?? p.productId,
            name: p.name ?? p.productName,
            sku: p.sku ?? p.productCode ?? '',
            unitOfMeasure: p.unitOfMeasure ?? '',
          }));
        const mapUnit = (d: any): UnitOption[] =>
          (d?.data?.data ?? d?.data ?? []).map((u: any) => ({ id: u.id ?? u.unitId, unitName: u.unitName ?? u.name }));

        setProducts(mapProd(prodRes.data));
        setUnits(mapUnit(unitRes.data));

        if (isEdit && id) {
          const stRes = await stockTransferService.getById(id);
          const st    = stRes.data?.data;
          if (st) {
            setSourceWarehouseId(st.sourceWarehouseId);
            setDestinationWarehouseId(st.destinationWarehouseId);
            setTransferDate(st.transferDate.split('T')[0]);
            setExpectedArrivalDate(st.expectedArrivalDate ? st.expectedArrivalDate.split('T')[0] : '');
            setNotes(st.notes ?? '');
            if (st.items?.length) {
              setItems(
                st.items.map((item) => ({
                  stockTransferItemId: item.id,
                  productId:           item.productId,
                  unitId:              item.unitId,
                  quantityRequested:   item.quantityRequested,
                  remarks:             item.remarks ?? '',
                }))
              );
            }
          }
        }
      } catch {
        setError(t('stockTransfer.failedToLoad'));
      } finally {
        setInitialLoad(false);
      }
    };
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Item handlers ──────────────────────────────────────────────────────────

  const handleItemChange = (index: number, field: keyof LineItem, value: unknown) => {
    setItems((prev) => {
      const next = [...prev];
      (next[index] as Record<string, unknown>)[field] = value;

      // Auto-select unit when product changes
      if (field === 'productId') {
        const selectedProduct = products.find((p) => p.id === Number(value));
        if (selectedProduct?.unitOfMeasure) {
          const matchedUnit = units.find(
            (u) => u.unitName.toLowerCase() === selectedProduct.unitOfMeasure!.toLowerCase()
          );
          if (matchedUnit) next[index].unitId = matchedUnit.id;
        }
      }

      return next;
    });
    setFieldErrors((prev) => ({ ...prev, items: undefined }));
  };

  const addLine    = () => setItems((prev) => [...prev, emptyLine()]);
  const removeLine = (i: number) => setItems((prev) => prev.filter((_, idx) => idx !== i));

  // ── Validation ─────────────────────────────────────────────────────────────

  const validate = (): boolean => {
    const errors: FieldErrors = {};
    let isValid = true;

    if (sourceWarehouseId <= 0) {
      errors.sourceWarehouse = t('stockTransfer.sourceRequired');
      isValid = false;
    }

    if (destinationWarehouseId <= 0) {
      errors.destinationWarehouse = t('stockTransfer.destinationRequired');
    } else if (sourceWarehouseId > 0 && sourceWarehouseId === destinationWarehouseId) {
      errors.destinationWarehouse = t('stockTransfer.sameWarehouseError');
      isValid = false;
    }
    if (errors.destinationWarehouse) isValid = false;

    if (!transferDate) {
      errors.transferDate = t('stockTransfer.dateRequired');
      isValid = false;
    }

    if (expectedArrivalDate && transferDate && expectedArrivalDate < transferDate) {
      errors.expectedArrivalDate = t('stockTransfer.arrivalBeforeTransferDate');
      isValid = false;
    }

    const validItems = items.filter((i) => i.productId > 0 && i.unitId > 0 && i.quantityRequested > 0);
    if (validItems.length === 0) {
      errors.items = t('stockTransfer.itemsRequired');
      isValid = false;
    }

    setFieldErrors(errors);

    if (!isValid) {
      const firstError = Object.values(errors).find((e) => e);
      if (firstError) setError(firstError);
    }

    return isValid;
  };

  // ── Submit ─────────────────────────────────────────────────────────────────

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!validate()) return;

    const validItems = items.filter((i) => i.productId > 0 && i.unitId > 0 && i.quantityRequested > 0);

    setLoading(true);
    try {
      if (isEdit && id) {
        await stockTransferService.update(id, {
          sourceWarehouseId,
          destinationWarehouseId,
          transferDate,
          expectedArrivalDate: expectedArrivalDate || undefined,
          notes:               notes || undefined,
          requestedByUserId:   user?.id ?? 0,
          items:               validItems.map((i) => ({
            productId:         i.productId,
            unitId:            i.unitId,
            quantityRequested: i.quantityRequested,
            remarks:           i.remarks || undefined,
          })),
        });
        navigate(`/stock-transfers/${id}`, {
          state: { message: t('stockTransfer.updatedSuccess') },
        });
      } else {
        const payload: CreateStockTransferRequest = {
          sourceWarehouseId,
          destinationWarehouseId,
          transferDate,
          expectedArrivalDate: expectedArrivalDate || undefined,
          notes:               notes || undefined,
          requestedByUserId:   user?.id ?? 0,
          items:               validItems.map((i) => ({
            productId:         i.productId,
            unitId:            i.unitId,
            quantityRequested: i.quantityRequested,
            remarks:           i.remarks || undefined,
          })),
        };
        const res     = await stockTransferService.create(payload);
        const created = res.data?.data;
        navigate(`/stock-transfers/${created?.id}`, {
          state: { message: t('stockTransfer.createdSuccess', { number: created?.transferNumber }) },
        });
      }
    } catch (err: unknown) {
      setError(parseApiError(err, isEdit ? t('stockTransfer.failedToUpdate') : t('stockTransfer.failedToCreate')).message);
    } finally {
      setLoading(false);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  if (initialLoad) {
    return (
      <Layout>
        <div className="st-page" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 300 }}>
          <div className="ims-spinner" />
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <form onSubmit={handleSubmit} className="st-create-page" noValidate>

        {/* ── Top bar ── */}
        <div className="ims-top-bar">
          <div className="ims-top-bar-left">
            <button type="button" className="ims-back-btn" onClick={() => navigate('/stock-transfers')}>
              <ArrowLeft size={13} strokeWidth={2.5} />
              {t('stockTransfer.backToList')}
            </button>
            <div>
              <h1 className="ims-page-title">
                {isEdit ? t('stockTransfer.editTitle') : t('stockTransfer.createTitle')}
              </h1>
              <p className="ims-page-subtitle">
                {isEdit ? t('stockTransfer.editSubtitle') : t('stockTransfer.createSubtitle')}
              </p>
            </div>
          </div>
          <div className="ims-top-bar-right">
            <button type="button" className="btn-cancel" onClick={() => navigate('/stock-transfers')} disabled={loading}>
              {t('common.cancel')}
            </button>
            <button type="submit" className="btn-save" disabled={loading}>
              {loading ? (
                <><span className="ims-btn-spinner" />{t('common.saving')}</>
              ) : (
                <>
                  <Save size={14} strokeWidth={2} />
                  {isEdit ? t('common.saveChanges') : t('stockTransfer.createTransfer')}
                </>
              )}
            </button>
          </div>
        </div>

        {/* ── Error banner ── */}
        {error && (
          <div className="ims-error-banner ims-alert ims-alert-error" role="alert">
            <AlertCircle size={15} strokeWidth={2} style={{ flexShrink: 0 }} />
            <span>{error}</span>
            <button
              type="button"
              onClick={() => setError(null)}
              style={{ marginInlineStart: 'auto', background: 'none', border: 'none', cursor: 'pointer', color: 'inherit' }}
              aria-label={t('common.close')}
            >✕</button>
          </div>
        )}

        {/* ── 3-column body ── */}
        <div className="ims-create-body">

          {/* ─── LEFT — Transfer Details ─── */}
          <div className="ims-panel-left">
            <div className="ims-card">
              <div className="ims-card-header">
                <h2 className="ims-card-title">{t('stockTransfer.transferDetails')}</h2>
              </div>
              <div className="ims-card-body">

                {/* Source Warehouse */}
                <div className="ims-form-group">
                  <label className="ims-form-label">
                    {t('stockTransfer.sourceWarehouse')} <span className="required">*</span>
                  </label>
                  <select
                    className={`ims-form-control${fieldErrors.sourceWarehouse ? ' is-invalid' : ''}`}
                    value={sourceWarehouseId}
                    onChange={(e) => {
                      setSourceWarehouseId(Number(e.target.value));
                      setFieldErrors((prev) => ({ ...prev, sourceWarehouse: undefined }));
                      setError(null);
                    }}
                  >
                    <option value={0}>{t('stockTransfer.selectSourceWarehouse')}</option>
                    {warehouses.map((w) => (
                      <option key={w.id} value={w.id}>{w.name}</option>
                    ))}
                  </select>
                  {fieldErrors.sourceWarehouse && (
                    <small className="form-error">{fieldErrors.sourceWarehouse}</small>
                  )}
                </div>

                {/* Arrow indicator */}
                <div style={{ textAlign: 'center', fontSize: '1.25rem', color: 'var(--ims-accent)', margin: '4px 0 8px' }}>↓</div>

                {/* Destination Warehouse */}
                <div className="ims-form-group">
                  <label className="ims-form-label">
                    {t('stockTransfer.destinationWarehouse')} <span className="required">*</span>
                  </label>
                  <select
                    className={`ims-form-control${fieldErrors.destinationWarehouse ? ' is-invalid' : ''}`}
                    value={destinationWarehouseId}
                    onChange={(e) => {
                      setDestinationWarehouseId(Number(e.target.value));
                      setFieldErrors((prev) => ({ ...prev, destinationWarehouse: undefined }));
                      setError(null);
                    }}
                  >
                    <option value={0}>{t('stockTransfer.selectDestinationWarehouse')}</option>
                    {warehouses
                      .filter((w) => w.id !== sourceWarehouseId)
                      .map((w) => (
                        <option key={w.id} value={w.id}>{w.name}</option>
                      ))}
                  </select>
                  {fieldErrors.destinationWarehouse && (
                    <small className="form-error">{fieldErrors.destinationWarehouse}</small>
                  )}
                </div>

                {/* Transfer Date */}
                <div className="ims-form-group">
                  <label className="ims-form-label">
                    {t('stockTransfer.transferDate')} <span className="required">*</span>
                  </label>
                  <input
                    type="date"
                    className={`ims-form-control${fieldErrors.transferDate ? ' is-invalid' : ''}`}
                    value={transferDate}
                    onChange={(e) => {
                      setTransferDate(e.target.value);
                      setFieldErrors((prev) => ({ ...prev, transferDate: undefined, expectedArrivalDate: undefined }));
                      setError(null);
                    }}
                  />
                  {fieldErrors.transferDate && (
                    <small className="form-error">{fieldErrors.transferDate}</small>
                  )}
                </div>

                {/* Expected Arrival Date */}
                <div className="ims-form-group">
                  <label className="ims-form-label">{t('stockTransfer.expectedArrivalDate')}</label>
                  <input
                    type="date"
                    className={`ims-form-control${fieldErrors.expectedArrivalDate ? ' is-invalid' : ''}`}
                    value={expectedArrivalDate}
                    onChange={(e) => {
                      setExpectedArrivalDate(e.target.value);
                      setFieldErrors((prev) => ({ ...prev, expectedArrivalDate: undefined }));
                      setError(null);
                    }}
                    min={transferDate}
                  />
                  {fieldErrors.expectedArrivalDate && (
                    <small className="form-error">{fieldErrors.expectedArrivalDate}</small>
                  )}
                </div>

                {/* Notes */}
                <div className="ims-form-group">
                  <label className="ims-form-label">{t('stockTransfer.notes')}</label>
                  <textarea
                    className="ims-form-control ims-textarea"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    rows={3}
                    placeholder={t('stockTransfer.notesPlaceholder')}
                  />
                </div>

              </div>
            </div>
          </div>

          {/* ─── CENTER — Transfer Items ─── */}
          <div className="ims-panel-center">
            <div className={`ims-card ims-lines-card${fieldErrors.items ? ' ims-card--invalid' : ''}`}>
              <div className="ims-lines-header">
                <h2 className="ims-card-title">{t('stockTransfer.transferItems')}</h2>
                <button
                  type="button"
                  className="ims-btn ims-btn-secondary"
                  style={{ padding: '6px 12px', fontSize: 12 }}
                  onClick={addLine}
                >
                  <Plus size={12} strokeWidth={2.5} />
                  {t('stockTransfer.addItem')}
                </button>
              </div>

              {fieldErrors.items && (
                <div style={{ padding: '6px 16px' }}>
                  <small className="form-error">{fieldErrors.items}</small>
                </div>
              )}

              <div className="ims-lines-body">
                {items.length === 0 ? (
                  <div className="ims-items-empty">
                    <div className="ims-items-empty-icon">🔄</div>
                    {t('stockTransfer.addItemsHint')}
                  </div>
                ) : (
                  <div className="ims-table-scroll">
                    <table className="ims-table">
                      <thead>
                        <tr>
                          <th className="ims-th" style={{ minWidth: 200 }}>{t('stockTransfer.product')}</th>
                          <th className="ims-th" style={{ minWidth: 120 }}>{t('stockTransfer.unit')}</th>
                          <th className="ims-th ims-th--num" style={{ minWidth: 120 }}>{t('stockTransfer.quantityRequested')}</th>
                          <th className="ims-th" style={{ minWidth: 180 }}>{t('stockTransfer.remarks')}</th>
                          <th className="ims-th ims-th--del" />
                        </tr>
                      </thead>
                      <tbody>
                        {items.map((item, index) => {
                          // A row is invalid if it has any field set but is incomplete
                          const rowTouched = item.productId > 0 || item.unitId > 0 || item.quantityRequested > 0;
                          const rowInvalid = rowTouched && (item.productId <= 0 || item.unitId <= 0 || item.quantityRequested <= 0);
                          return (
                            <tr key={index} className={`ims-tr${rowInvalid ? ' ims-tr--invalid' : ''}`}>
                              {/* Product */}
                              <td className="ims-td">
                                <select
                                  className={`ims-cell-input${rowInvalid && item.productId <= 0 ? ' is-invalid' : ''}`}
                                  value={item.productId}
                                  onChange={(e) => handleItemChange(index, 'productId', Number(e.target.value))}
                                >
                                  <option value={0}>{t('stockTransfer.selectProduct')}</option>
                                  {products.map((p) => (
                                    <option key={p.id} value={p.id}>{p.name} ({p.sku})</option>
                                  ))}
                                </select>
                              </td>
                              {/* Unit */}
                              <td className="ims-td">
                                <select
                                  className={`ims-cell-input${rowInvalid && item.unitId <= 0 ? ' is-invalid' : ''}`}
                                  value={item.unitId}
                                  onChange={(e) => handleItemChange(index, 'unitId', Number(e.target.value))}
                                >
                                  <option value={0}>{t('stockTransfer.selectUnit')}</option>
                                  {units.map((u) => (
                                    <option key={u.id} value={u.id}>{u.unitName}</option>
                                  ))}
                                </select>
                              </td>
                              {/* Qty Requested */}
                              <td className="ims-td ims-td--num">
                                <input
                                  type="number"
                                  className={`ims-cell-input ims-num-input${rowInvalid && item.quantityRequested <= 0 ? ' is-invalid' : ''}`}
                                  step="0.01"
                                  min={0.01}
                                  value={item.quantityRequested || ''}
                                  onChange={(e) => handleItemChange(index, 'quantityRequested', parseFloat(e.target.value) || 0)}
                                  placeholder="0"
                                />
                              </td>
                              {/* Remarks */}
                              <td className="ims-td">
                                <input
                                  type="text"
                                  className="ims-cell-input"
                                  value={item.remarks}
                                  onChange={(e) => handleItemChange(index, 'remarks', e.target.value)}
                                  placeholder={t('stockTransfer.remarksPlaceholder')}
                                />
                              </td>
                              {/* Delete */}
                              <td className="ims-td ims-td--del">
                                <button
                                  type="button"
                                  className="ims-remove-btn"
                                  onClick={() => removeLine(index)}
                                  disabled={items.length === 1}
                                  title={t('common.remove')}
                                >
                                  <X size={12} strokeWidth={2.5} />
                                </button>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* ─── RIGHT — Requested By + Tips ─── */}
          <div className="ims-panel-right">

            {/* Requested By */}
            <div className="ims-card">
              <div className="ims-card-header">
                <h3 className="ims-card-title">{t('stockTransfer.requestedBy')}</h3>
              </div>
              <div className="ims-card-body">
                <div className="ims-user-row">
                  <div className="ims-user-avatar">
                    {user?.firstName?.[0]?.toUpperCase() ?? '?'}
                  </div>
                  <div>
                    <div className="ims-user-name">{user?.firstName} {user?.lastName}</div>
                    <div className="ims-user-email">{user?.email}</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Route preview */}
            {(sourceWarehouseId > 0 || destinationWarehouseId > 0) && (
              <div className="ims-card">
                <div className="ims-card-header">
                  <h3 className="ims-card-title">{t('stockTransfer.transferRoute')}</h3>
                </div>
                <div className="ims-card-body">
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'center', textAlign: 'center' }}>
                    <div style={{
                      padding: '8px 14px', borderRadius: 8,
                      background: 'var(--ims-accent-bg)', color: 'var(--ims-accent)',
                      fontWeight: 600, fontSize: '0.875rem', width: '100%',
                    }}>
                      {sourceWarehouseId > 0 ? warehouses.find((w) => w.id === sourceWarehouseId)?.name : '—'}
                    </div>
                    <div style={{ fontSize: '1.25rem', color: 'var(--ims-accent)' }}>↓</div>
                    <div style={{
                      padding: '8px 14px', borderRadius: 8,
                      background: '#dcfce7', color: '#166534',
                      fontWeight: 600, fontSize: '0.875rem', width: '100%',
                    }}>
                      {destinationWarehouseId > 0 ? warehouses.find((w) => w.id === destinationWarehouseId)?.name : '—'}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Tips */}
            <div className="ims-card">
              <div className="ims-card-header">
                <h3 className="ims-card-title">{t('stockTransfer.tipsTitle')}</h3>
              </div>
              <div className="ims-card-body">
                <ul className="ims-help-list">
                  <li>{t('stockTransfer.tip1')}</li>
                  <li>{t('stockTransfer.tip2')}</li>
                  <li>{t('stockTransfer.tip3')}</li>
                </ul>
              </div>
            </div>

          </div>

        </div>
      </form>
    </Layout>
  );
};

export default StockTransferCreatePage;
